import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import AddressInput from '../components/ui/AddressInput';
import { Building2, Mail, Lock, Phone, Clock, Check, ArrowRight, Sparkles, MapPin, Eye, EyeOff } from 'lucide-react';
import Logo from '../components/Logo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClinicRegistration() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    // P0 - Must have
    clinicName: '',
    adminEmail: '',
    adminPassword: '',
    adminName: '',
    // P1 - Important
    phone: '',
    address: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    // P2 - Nice to have (optional)
    subdomain: ''
  });

  const timezones = [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'America/Phoenix',
    'Europe/London',
    'Europe/Paris',
    'Asia/Tokyo'
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Auto-generate subdomain from clinic name
    if (field === 'clinicName' && !formData.subdomain) {
      let slug = value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      // Ensure subdomain is within 3-20 character limit
      if (slug.length > 20) {
        slug = slug.substring(0, 20).replace(/-$/, '');
      }
      if (slug.length < 3) {
        slug = slug + 'clinic';
      }
      setFormData(prev => ({ ...prev, subdomain: slug }));
    }
  };

  const validateStep1 = () => {
    if (!formData.clinicName.trim()) {
      toast.error('Please enter your clinic name');
      return false;
    }
    if (!formData.adminEmail.trim()) {
      toast.error('Please enter your email address');
      return false;
    }
    if (!formData.adminEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return false;
    }
    if (!formData.adminPassword || formData.adminPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return false;
    }
    if (!formData.adminName.trim()) {
      toast.error('Please enter your name');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.phone.trim()) {
      toast.error('Please enter your clinic phone number');
      return false;
    }
    if (!formData.address.trim()) {
      toast.error('Please enter your clinic address');
      return false;
    }
    return true;
  };

  const handleNextStep = () => {
    if (step === 1 && validateStep1()) {
      setStep(2);
    } else if (step === 2 && validateStep2()) {
      setStep(3);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep2()) return;
    
    setLoading(true);
    
    try {
      const response = await axios.post(`${API_URL}/api/public/clinic-registration/register`, {
        clinic_name: formData.clinicName,
        subdomain: formData.subdomain,
        admin_email: formData.adminEmail,
        admin_password: formData.adminPassword,
        admin_name: formData.adminName,
        contact_email: formData.adminEmail, // Use admin email as contact email
        contact_phone: formData.phone,
        address: formData.address,
        subscription_tier: "starter"
      });

      if (response.data.success) {
        toast.success('🎉 Welcome! Your clinic is ready!');
        
        // Auto-login
        if (response.data.access_token && response.data.user) {
          localStorage.setItem('token', response.data.access_token);
          localStorage.setItem('user', JSON.stringify(response.data.user));
          
          // Set first login flag to show welcome dashboard
          localStorage.setItem(`first_login_${response.data.user.id}`, 'true');
          
          // Redirect to admin dashboard
          setTimeout(() => {
            window.location.href = '/admin';
          }, 1500);
        } else {
          // Fallback: redirect to login if no token
          setTimeout(() => {
            navigate('/login');
          }, 1500);
        }
      }
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-blue-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-600">Already have an account?</span>
            <Link to="/login">
              <Button variant="outline">Sign In</Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Progress Indicator */}
      <div className="max-w-2xl mx-auto px-4 pt-8">
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2, 3].map((s) => (
            <React.Fragment key={s}>
              <div className={`flex items-center justify-center w-10 h-10 rounded-full font-semibold ${
                s === step ? 'bg-teal-600 text-white' :
                s < step ? 'bg-teal-100 text-teal-600' : 
                'bg-gray-100 text-gray-400'
              }`}>
                {s < step ? <Check className="h-5 w-5" /> : s}
              </div>
              {s < 3 && (
                <div className={`h-1 w-16 ${s < step ? 'bg-teal-600' : 'bg-gray-200'}`} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-2xl mx-auto px-4 pb-12">
        <Card className="shadow-lg">
          <CardHeader className="text-center">
            {step === 1 && (
              <>
                <div className="mx-auto w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-4">
                  <Building2 className="h-8 w-8 text-teal-600" />
                </div>
                <CardTitle className="text-2xl">Create Your Clinic Account</CardTitle>
                <CardDescription>
                  Start your 30-day free trial. No credit card required.
                </CardDescription>
                <Badge className="mx-auto mt-2 bg-green-100 text-green-800">
                  <Sparkles className="h-3 w-3 mr-1" />
                  Free 30-Day Trial
                </Badge>
              </>
            )}
            
            {step === 2 && (
              <>
                <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <MapPin className="h-8 w-8 text-blue-600" />
                </div>
                <CardTitle className="text-2xl">Tell Us About Your Clinic</CardTitle>
                <CardDescription>
                  Help us personalize your experience
                </CardDescription>
              </>
            )}
            
            {step === 3 && (
              <>
                <div className="mx-auto w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                  <Check className="h-8 w-8 text-purple-600" />
                </div>
                <CardTitle className="text-2xl">You&apos;re Almost Ready!</CardTitle>
                <CardDescription>
                  Review and confirm your details
                </CardDescription>
              </>
            )}
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Step 1: Account Setup */}
            {step === 1 && (
              <div className="space-y-4">
                <div>
                  <Label htmlFor="clinicName">Clinic Name *</Label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="clinicName"
                      placeholder="e.g., Wellness Medical Center"
                      value={formData.clinicName}
                      onChange={(e) => handleChange('clinicName', e.target.value)}
                      className="pl-10"
                      autoFocus
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="adminName">Your Full Name *</Label>
                  <Input
                    id="adminName"
                    placeholder="e.g., Dr. Sarah Johnson"
                    value={formData.adminName}
                    onChange={(e) => handleChange('adminName', e.target.value)}
                  />
                </div>

                <div>
                  <Label htmlFor="adminEmail">Your Email Address *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="adminEmail"
                      type="email"
                      placeholder="admin@yourclinic.com"
                      value={formData.adminEmail}
                      onChange={(e) => handleChange('adminEmail', e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">You&apos;ll use this to sign in</p>
                </div>

                <div>
                  <Label htmlFor="adminPassword">Create Password *</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="adminPassword"
                      type={showPassword ? "text" : "password"}
                      placeholder="At least 8 characters"
                      value={formData.adminPassword}
                      onChange={(e) => handleChange('adminPassword', e.target.value)}
                      className="pl-10 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => navigate('/')}
                  >
                    Back to Home
                  </Button>
                  <Button 
                    className="flex-1" 
                    size="lg"
                    onClick={handleNextStep}
                  >
                    Continue
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 2: Clinic Details */}
            {step === 2 && (
              <div className="space-y-4">
                <div>
                  <Label htmlFor="phone">Clinic Phone Number *</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="(555) 123-4567"
                      value={formData.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      className="pl-10"
                      autoFocus
                    />
                  </div>
                </div>

                <div>
                  <AddressInput
                    id="address"
                    name="address"
                    label="Clinic Address *"
                    placeholder="Start typing your clinic address..."
                    value={formData.address}
                    onChange={(e) => handleChange('address', e.target.value)}
                    onAddressSelected={(addressData) => {
                      // Update address with the full formatted address
                      handleChange('address', addressData.formatted_address);
                    }}
                    required
                    restrictToCountry="us"
                  />
                </div>

                <div>
                  <Label htmlFor="timezone">Timezone *</Label>
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <select
                      id="timezone"
                      value={formData.timezone}
                      onChange={(e) => handleChange('timezone', e.target.value)}
                      className="w-full px-3 py-2 pl-10 border rounded-md"
                    >
                      {timezones.map(tz => (
                        <option key={tz} value={tz}>{tz}</option>
                      ))}
                    </select>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">For scheduling and reminders</p>
                </div>

                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => setStep(1)}
                  >
                    Back
                  </Button>
                  <Button 
                    className="flex-1" 
                    size="lg"
                    onClick={handleNextStep}
                  >
                    Review & Finish
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Review & Submit */}
            {step === 3 && (
              <div className="space-y-6">
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm text-gray-500">Clinic Name</p>
                      <p className="font-semibold">{formData.clinicName}</p>
                    </div>
                  </div>
                  
                  <div className="border-t pt-3">
                    <p className="text-sm text-gray-500">Administrator</p>
                    <p className="font-semibold">{formData.adminName}</p>
                    <p className="text-sm text-gray-600">{formData.adminEmail}</p>
                  </div>
                  
                  <div className="border-t pt-3">
                    <p className="text-sm text-gray-500">Contact</p>
                    <p className="font-semibold">{formData.phone}</p>
                    <p className="text-sm text-gray-600">{formData.address}</p>
                  </div>
                  
                  <div className="border-t pt-3">
                    <p className="text-sm text-gray-500">Timezone</p>
                    <p className="font-semibold">{formData.timezone}</p>
                  </div>
                </div>

                <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Sparkles className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-teal-900">Your 30-Day Free Trial Includes:</p>
                      <ul className="text-sm text-teal-800 mt-2 space-y-1">
                        <li>✓ Unlimited members</li>
                        <li>✓ Appointment scheduling</li>
                        <li>✓ Payment processing</li>
                        <li>✓ Email notifications</li>
                        <li>✓ Full platform access</li>
                      </ul>
                      <p className="text-xs text-teal-700 mt-2">
                        No credit card required. Cancel anytime.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => setStep(2)}
                    disabled={loading}
                  >
                    Back
                  </Button>
                  <Button 
                    className="flex-1" 
                    size="lg"
                    onClick={handleSubmit}
                    disabled={loading}
                  >
                    {loading ? 'Creating Account...' : 'Start Free Trial'}
                  </Button>
                </div>

                <p className="text-xs text-center text-gray-500">
                  By creating an account, you agree to our{' '}
                  <a href="/terms-of-service" className="text-teal-600 hover:underline">Terms of Service</a>
                  {' '}and{' '}
                  <a href="/privacy-policy" className="text-teal-600 hover:underline">Privacy Policy</a>
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Trust Indicators */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500 mb-4">Trusted by healthcare providers nationwide</p>
          <div className="flex items-center justify-center gap-8 text-xs text-gray-400">
            <div className="flex items-center gap-1">
              <Lock className="h-4 w-4" />
              <span>HIPAA Compliant</span>
            </div>
            <div className="flex items-center gap-1">
              <Check className="h-4 w-4" />
              <span>PCI DSS Level 1</span>
            </div>
            <div className="flex items-center gap-1">
              <Check className="h-4 w-4" />
              <span>256-bit Encryption</span>
            </div>
          </div>
          
          {/* Links */}
          <div className="mt-6 space-y-2">
            <p className="text-sm text-gray-500">
              Already have an account?{' '}
              <Link to="/login" className="text-teal-600 hover:text-teal-700 font-medium">
                Sign in here
              </Link>
            </p>
            <p className="text-sm text-gray-500">
              Are you a patient?{' '}
              <Link to="/register-patient" className="text-teal-600 hover:text-teal-700 font-medium">
                Register as a member
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
